/**
 * Payment-type HTTP request schemas — SCRUM-290 (DS-021 §3.4 / §15, Payments BC slice 2).
 *
 * Wire-shape guards for the five payment-type catalog management endpoints:
 *   POST   /organizations/:slug/payment-types       → createCustomPaymentTypeSchema
 *   PATCH  /organizations/:slug/payment-types/:id   → updatePaymentTypeSchema
 *   PUT    /organizations/:slug/payment-types/:id/status         → setPaymentTypeStatusSchema
 *   PUT    /organizations/:slug/payment-types/system/:key/status → setPaymentTypeStatusSchema
 *   DELETE /organizations/:slug/payment-types/:id   (no body)
 *
 * Design notes:
 *   - `baseSystemType` is re-declared here as a wire enum (the four manual-rail
 *     custom-mapping targets). DO NOT import domain code — the validators package
 *     is the schema source of truth (CLAUDE.md rule 3); the domain `isBaseSystemType`
 *     guard enforces the same constraint at the UC boundary. CC/ACH are gateway-
 *     integrated and are NOT valid custom-mapping targets; they are excluded from this
 *     enum. If an unknown value is submitted it is rejected at the wire boundary
 *     before reaching the domain.
 *   - `status` is re-declared here as a wire enum (Active | Inactive). The domain
 *     `PaymentTypeStatus` carries the identical values.
 *   - `processor` is optional/nullable with a max-length guard. Free text, no format
 *     constraint — the UC accepts any non-empty string (or null to clear).
 *   - The schema uses `.strict()` to reject unknown keys (defense-in-depth, mirrors
 *     createGuestSchema).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Wire-enum constants (NOT imported from domain — schema is the wire source of truth)
// ---------------------------------------------------------------------------
/** The four manual-rail baseSystemType values valid for a CUSTOM payment type. */
export const BASE_SYSTEM_TYPE_WIRE_VALUES = ['Cash', 'Owner', 'Adjustment', 'Channel'];
/** PaymentType lifecycle statuses. */
export const PAYMENT_TYPE_STATUS_WIRE_VALUES = ['Active', 'Inactive'];
// ---------------------------------------------------------------------------
// Bounds
// ---------------------------------------------------------------------------
const PAYMENT_NAME_MAX = 120;
const PROCESSOR_MAX = 100;
// ---------------------------------------------------------------------------
// POST /organizations/:slug/payment-types
// ---------------------------------------------------------------------------
export const createCustomPaymentTypeSchema = z
    .object({
    /** Display name for the custom payment type. Non-empty, trimmed, max 120 chars. */
    paymentName: z.string().min(1).max(PAYMENT_NAME_MAX).trim(),
    /** One of the four manual-rail custom-mapping targets. CC/ACH are gateway-
     *  integrated and are NOT accepted here (domain also enforces this). */
    baseSystemType: z.enum(BASE_SYSTEM_TYPE_WIRE_VALUES),
    /** Whether this payment type requires a matching refund when reversed. */
    requiresRefund: z.boolean(),
    /** Optional gateway processor identifier (e.g. "stripe_pm_xxx"). */
    processor: z.string().min(1).max(PROCESSOR_MAX).nullable().optional(),
    /** Initial lifecycle status. Defaults to Active when omitted. */
    status: z.enum(PAYMENT_TYPE_STATUS_WIRE_VALUES).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// PATCH /organizations/:slug/payment-types/:id
// ---------------------------------------------------------------------------
export const updatePaymentTypeSchema = z
    .object({
    /** Updated display name. When absent, the stored name is retained. */
    paymentName: z.string().min(1).max(PAYMENT_NAME_MAX).trim().optional(),
    /** Whether this payment type requires a matching refund. */
    requiresRefund: z.boolean().optional(),
    /** Updated processor reference. `null` clears an existing value. */
    processor: z.string().min(1).max(PROCESSOR_MAX).nullable().optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// PUT /organizations/:slug/payment-types/:id/status
// PUT /organizations/:slug/payment-types/system/:key/status
// ---------------------------------------------------------------------------
export const setPaymentTypeStatusSchema = z
    .object({
    /** Target lifecycle status (Active to reactivate; Inactive to deactivate). */
    status: z.enum(PAYMENT_TYPE_STATUS_WIRE_VALUES),
})
    .strict();
//# sourceMappingURL=payment-type.schema.js.map